import React, { useState } from 'react';
import {
	SypacButton,
	SypacIcon,
	SypacInput,
	SypacText,
} from '@sypac/component-library-react';
import { useDetectClickOutside } from 'react-detect-click-outside';
import { T, useTranslate } from '@tolgee/react';
import { SearchLocationProps } from '../clientSelectLocation.interface';
import { Geo } from '../../../../interfaces/geo.interface';
import Close from '../../../../assets/Close';
import { cn } from '../../../../lib/utils';
import {
	addressWithoutCityCountry,
	cityCountryOnly,
	formatAddress,
} from '../../../../utils/string.util';
import StoreSearchPin from '../../../../assets/StoreSearchPin';
import NextStepArrow from '../../../../assets/NextStepArrow';
import HighlightText from '../../../../components/HighlightText/HighlightText';

const SearchLocation: React.FC<SearchLocationProps> = ({
	inputName,
	search,
	locations,
	locationsLoading,
	hasSearched,
	selectedLocation,
	onChange,
	selectLocation,
	onPreviewLocation,
	isMapSelection,
	setIsMapSelection,
	placeholder,
}) => {
	const { t } = useTranslate();
	const [expandResults, setExpandResults] = useState<boolean>(false);
	const inputRef = useDetectClickOutside({
		onTriggered: () => setExpandResults(false),
	});

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		onChange(e.target.value);
		setExpandResults(true);

		if (setIsMapSelection) {
			setIsMapSelection(false);
		}
	};

	const handleLocationItemClick = (loc: Geo) => {
		setExpandResults(false);
		onChange(formatAddress(loc.address));
		onPreviewLocation(loc);

		if (setIsMapSelection) {
			setIsMapSelection(false);
		}
	};

	const handleClear = () => {
		setExpandResults(false);
		onChange('');
		onPreviewLocation(null);

		if (setIsMapSelection) {
			setIsMapSelection(false);
		}
	};

	const handleConfirm = () => {
		if (!selectedLocation) return;
		selectLocation(selectedLocation);
	};

	const shouldShowDropdown =
		expandResults &&
		search &&
		!locationsLoading &&
		hasSearched &&
		!isMapSelection;

	return (
		<>
			<div className="relative w-full group" ref={inputRef}>
				<SypacInput>
					<input
						name={inputName}
						autoComplete="off"
						type="text"
						value={search}
						className={cn(
							'w-full h-[42px] 2xl:h-[62px] outline-none border border-gray-10 rounded-10 pl-[48px]',
							'truncate text-gray-80 text-[16px] leading-[22px] caret-gray-80 placeholder:leading-[22px] placeholder:text-[16px] placeholder:text-gray-40',
							'transition hover:border-gray-30 focus:border-primary-violet',
							search && 'pr-[120px]',
						)}
						placeholder={
							placeholder
								? t(`searchLocation.${placeholder.key}`, `${placeholder.label}`)
								: t(
										'searchLocation.enterThePlaceWhere',
										'Enter the place where you would like to receive your products',
								  )
						}
						onFocus={() => {
							if (!isMapSelection) {
								setExpandResults(true);
							}
						}}
						onChange={handleInputChange}
					/>
				</SypacInput>

				<span className="absolute top-[9px] 2xl:top-[18px] left-3">
					<StoreSearchPin isActive={expandResults || !!selectedLocation} />
				</span>

				<div
					className={cn(
						'absolute top-[9px] 2xl:top-[18px] right-[20px] flex justify-center items-center gap-3',
						selectedLocation && 'top-[9px] 2xl:top-[17px]',
					)}
				>
					<SypacIcon
						icon-name="Magnifer"
						className="text-gray-40"
						size="custom"
						width="24px"
						height="24px"
					/>

					{(search || selectedLocation) && (
						<>
							<div className="w-[1px] h-[12px] bg-gray-10" />
							<SypacButton variant="subTitle">
								<button
									type="button"
									className="w-[18px] h-[18px] p-0 m-0 flex justify-center items-center border border-solid border-gray-40 rounded-[4px]"
									onClick={handleClear}
								>
									<Close color="#919191" width="6" height="6" />
								</button>
							</SypacButton>
						</>
					)}
				</div>

				{shouldShowDropdown && (
					<div className="flex-col absolute top-[62px] md:top-[52px] 2xl:top-[70px] w-full max-h-72 bg-white border border-solid border-gray-10 rounded-[10px] overflow-hidden z-50 box-border shadow-2xl flex">
						{locations.length ? (
							<nav className="flex flex-col w-full p-[3px] pr-3 overflow-y-scroll overflow-x-hidden">
								{locations.map((location) => (
									<div
										key={location.locationId}
										className="w-[calc(100%-6px)] max-w-full box-border flex gap-3 items-start px-[6px] py-1 bg-white border border-solid border-white rounded-[8px] cursor-pointer hover:bg-[#FAFAFA] hover:border-[#E8E8E8] mb-[3px]"
										onClick={() => handleLocationItemClick(location)}
									>
										<StoreSearchPin />

										<div className="flex flex-col gap-[6px]">
											<SypacText
												variant="body-normal-medium"
												className="text-[16px] leading-[22px] text-gray-80"
											>
												<span>
													<HighlightText
														text={addressWithoutCityCountry(location.address)}
														query={search}
														type="noCity"
														highlightedText={(location as any).highlightedText}
													/>
												</span>
											</SypacText>

											<SypacText
												variant="body-regular-small"
												className="text-[12px] leading-[16px] text-gray-40"
											>
												<span>
													<HighlightText
														text={cityCountryOnly(location.address)}
														query={search}
														type="withCity"
														highlightedText={(location as any).highlightedText}
													/>
												</span>
											</SypacText>
										</div>
									</div>
								))}
							</nav>
						) : (
							<div className="flex items-center justify-center h-[48px]">
								<SypacText variant="body-normal-medium">
									<p>
										<T keyName="searchLocation.enterThePlaceWhere">
											Enter the place where you would like to receive your
											products
										</T>
									</p>
								</SypacText>
							</div>
						)}
					</div>
				)}
			</div>
			{selectedLocation && (
				<SypacButton variant="secondary" size="small" className="w-full">
					<button
						className="w-full flex items-center gap-3 px-15 py-[9px] rounded-[10px] transition border-0 bg-primary-violet hover:bg-kimberly"
						onClick={handleConfirm}
					>
						<NextStepArrow />
						<SypacText variant="body-regular-medium">
							<p className="text-white mt-[2px]">
								<T keyName="searchLocationInput.nextStep">Confirm address</T>
							</p>
						</SypacText>
					</button>
				</SypacButton>
			)}
		</>
	);
};

export default SearchLocation;
